import { isState } from "@snap/state-management";
import {
    isArrayBuffer,
    isArrayOfType,
    isFunction,
    isNumber,
    isRecord,
    isString,
    isTypedArray,
    isUndefined,
    predicateRecordValues,
} from "../common/typeguards";
import { Injectable } from "../dependency-injection/Injectable";
import { lensCoreFactory } from "../lens-core-module";
import { LensCoreModule, Uri, UriCancelRequest, UriRequest, UriResponse } from "../lens-core-module/generated-types";
import { Lens } from "../lens/Lens";
import { getLogger } from "../logger/logger";
import { LensKeyboard, lensKeyboardFactory } from "../session/LensKeyboard";
import { LensState, lensStateFactory } from "../session/lensState";

const logger = getLogger("UriHandlers");

const SEPARATOR = "://";

function extractSchemeAndRoute(uri: Uri) {
    const separatorIndex = uri.indexOf(SEPARATOR);
    const scheme = uri.slice(0, separatorIndex);
    const route = uri.slice(separatorIndex + SEPARATOR.length);
    return [scheme, route];
}

function isUri(value: unknown): value is Uri {
    return isString(value) && value.includes(SEPARATOR);
}

function isUriHandler(value: unknown): value is UriHandler {
    return (
        isRecord(value) &&
        (isUri(value.uri) || isArrayOfType(isUri, value.uri)) &&
        isFunction(value.handleRequest) &&
        (isUndefined(value.cancelRequest) || isFunction(value.cancelRequest))
    );
}

function isUriHandlers(value: unknown): value is UriHandlers {
    return isArrayOfType(isUriHandler, value);
}

function isUriResponse(value: unknown): value is UriResponse {
    return (
        isRecord(value) &&
        isNumber(value.code) &&
        isString(value.description) &&
        isString(value.contentType) &&
        (isArrayBuffer(value.data) || isTypedArray(value.data)) &&
        (isUndefined(value.metadata) || predicateRecordValues(isString)(value.metadata))
    );
}

/**
 * Provides a way for a lens to call into external services that work under HTTP-like protocol.
 */
export interface UriHandler {
    /**
     * [scheme]://[route] pattern that this handler processes or an array of patterns.
     */
    uri: Uri | Uri[];

    /**
     * Called to process the provided request.
     * @param request Lens request.
     * @param reply A callback function to send response back.
     * The function can be called none or multiple number of times.
     * @param lens Lens the request is coming from.
     */
    handleRequest(request: UriRequest, reply: (response: UriResponse) => void, lens: Lens): void;

    /**
     * Called to indicated that URI request initated earlier is cancelled.
     * @param request Lens request.
     * @param lens Lens the request is coming from.
     */
    cancelRequest?(request: UriCancelRequest, lens: Lens): void;
}

/**
 * Array of {@link UriHandler} objects.
 */
export type UriHandlers = UriHandler[];

/**
 * An extension point for client URI handlers.
 */
export const uriHandlersFactory = Injectable("UriHandlers", () => {
    const uriHandlers: UriHandlers = [];
    return uriHandlers;
});

/**
 * Registers URI handlers within LensCore.
 * @internal
 */
export const registerUriHandlers = Injectable(
    "registerUriHandlers",
    [lensCoreFactory.token, lensStateFactory.token, uriHandlersFactory.token, lensKeyboardFactory.token] as const,
    (lensCore: LensCoreModule, lensState: LensState, userHandlers: UriHandlers, lensKeyboard: LensKeyboard): void => {
        if (!isUriHandlers(userHandlers)) {
            throw new Error("Expected an array of UriHandler objects");
        }

        // Users may define UriHandlers using the uriHandlersFactory.token, but we need to add some internally-defined
        // handlers (e.g. lens keyboard handler) before registering handlers with LensCore.
        const allHandlers = userHandlers.concat(lensKeyboard.uriHandler);

        for (const { uri, handleRequest, cancelRequest } of allHandlers) {
            const uris = Array.isArray(uri) ? uri : [uri];
            for (const [scheme, route] of uris.map(extractSchemeAndRoute)) {
                lensCore.registerUriListener(scheme, route, {
                    handleRequest: (request) => {
                        const reply = (response: UriResponse) => {
                            if (!isUriResponse(response)) {
                                throw new Error("Expected UriResponse object");
                            }
                            lensCore.provideUriResponse(request.identifier, response);
                        };

                        // Since lenses are the only things that make URI requests, we expect to always be in the
                        // "lensApplied" state – we'll sanity check, though, and log a warning if we're not.
                        const state = lensState.getState();
                        if (isState(state, "noLensApplied")) {
                            logger.warn(
                                `Got a URI request for ${request.uri}, but there is no active lens. The ` +
                                    `request will not be processed.`
                            );
                            return;
                        }

                        // NOTE: we do not handle any error thrown on an extension side when handleRequest() is called.
                        // That responsibility is delegated to the extension by design and that is exactly what Android
                        // and iOS SDKs do.
                        handleRequest(request, reply, state.data);
                    },
                    cancelRequest: (request) => {
                        if (cancelRequest) {
                            const state = lensState.getState();
                            if (isState(state, "noLensApplied")) {
                                logger.warn(
                                    `Got a URI cancel request for ${request.uri}, but there is no active ` +
                                        `lens. The cancel request will not be processed.`
                                );
                                return;
                            }
                            cancelRequest(request, state.data);
                        }
                    },
                });
            }
        }
    }
);

export { Uri, UriCancelRequest, UriRequest, UriResponse } from "../lens-core-module/generated-types";
